/* eslint-disable no-console */
/* eslint-disable max-len */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';
import { MdCrop } from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import { TbReplace } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import NewDialogPopup from '../../../../../../../../../../../../components/Common/NewDialogPopup/NewDialogPopup';
import { LanguageContext } from '../../../../../../../../../../../../libs/Context';
import { s3DeleteNew } from '../../../../../../../../../../../../libs/HelperContent';
import { createPlaylist } from '../../../../../../../../../../../actions/playlist_management';
import UploadDialog from '../../../../../UploadDialog';

interface ThumbnailData {
	url: string;
	canEdit?: boolean;
	canDelete?: boolean;
	source?: string;
	key?: string;
}

interface PlaylistState {
	inputValue: string;
	description: string;
	isCreating: boolean;
	createdPlaylist: string | null;
	hover: boolean;
	uploadDialogOpen: boolean;
	deleteDialogOpen: boolean;
	thumbnail: ThumbnailData;
	currentSelectedPlaylists: any;
}

// Default thumbnail object to use when no thumbnail exists
const DEFAULT_THUMBNAIL: ThumbnailData = {
	url       : '',
	source    : 'system',
	canEdit   : true,
	canDelete : true
};

const CreatePlaylist = (props) =>
{
	const { data, onChange, handlCreateCallback = () => {} } = props;
	const { language } = useContext(LanguageContext);
	const { companyId } = useSelector((state: any) => state.mysession.session);

	// Initialize state with pre-selected playlists
	const [ state, setState ] = useState<PlaylistState>({
		inputValue               : Array.isArray(data) ? '' : data?.name || '',
		description              : Array.isArray(data) ? '' : data?.description || '',
		isCreating               : false,
		createdPlaylist          : null,
		hover                    : false,
		uploadDialogOpen         : false,
		deleteDialogOpen         : false,
		thumbnail                : Array.isArray(data) ? DEFAULT_THUMBNAIL : data?.thumbnail || DEFAULT_THUMBNAIL,
		currentSelectedPlaylists : [] // Will be populated when TreeView loads
	});

	// Destructure for easier access
	const {
		inputValue,
		description,
		isCreating,
		createdPlaylist,
		hover,
		uploadDialogOpen,
		deleteDialogOpen,
		thumbnail,
		currentSelectedPlaylists
	} = state;

	// Helper function to update state partially
	const updateState = useCallback((newState: Partial<PlaylistState>) =>
	{
		setState((prev) => ({ ...prev, ...newState }));
	}, []);

	// Character count tracking
	const descriptionMaxLength = 250;

	// Create playlist handler
	const handleCreatePlaylist = useCallback(async () =>
	{
		if (!inputValue.trim() || isCreating || createdPlaylist) return;

		updateState({ isCreating: true });

		try
		{
			const payload = {
				name      : inputValue,
				description,
				companyId,
				thumbnail : thumbnail?.url
					? {
						url       : thumbnail.url,
						source    : thumbnail.source,
						key       : thumbnail.key,
						canEdit   : thumbnail.canEdit,
						canDelete : thumbnail.canDelete
					}
					: undefined
			};

			const response = await createPlaylist(payload);

			if (response && response._id)
			{
				// updateState({ createdPlaylist: inputValue });
				updateState({ inputValue: '', description: '', thumbnail: DEFAULT_THUMBNAIL });

				// Update selected playlists to include the new one
				const updatedPlaylists = [ ...(currentSelectedPlaylists || []), response ];

				// updateState({ currentSelectedPlaylists: updatedPlaylists });

				// Call the create callback with the updated playlists data
				handlCreateCallback(updatedPlaylists);

				// Notify parent component with all playlists including the new one
				// if (onChange)
				// {
				// 	onChange(updatedPlaylists);
				// }

				// setCategoryName('');
				// setCharCount(0);
			}
			else
			{
				toast.error('Failed to create playlist: Invalid response from server');
			}
		}
		catch (error)
		{
			console.error('Error creating playlist:', error);
		}
		finally
		{
			updateState({ isCreating: false });
		}
	}, [ inputValue, description, isCreating, createdPlaylist, thumbnail, updateState, onChange, companyId, currentSelectedPlaylists ]);

	// Input change handler
	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
	{
		if (!createdPlaylist)
		{
			updateState({ inputValue: e.target.value });
		}
	}, [ createdPlaylist, updateState ]);

	// Description change handler
	const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) =>
	{
		updateState({ description: e.target.value });
	}, [ updateState ]);

	// Toggle hover state
	const toggleHover = useCallback((value: boolean) =>
	{
		updateState({ hover: value });
	}, [ updateState ]);

	// Open thumbnail settings

	// Handle thumbnail replacement
	const handleReplaceThumbnail = useCallback(() =>
	{
		updateState({ uploadDialogOpen: true });
	}, [ updateState ]);

	const handleOpenThumbnail = useCallback(() =>
	{
		handleReplaceThumbnail();
	}, []);

	// Handle thumbnail deletion
	const handleDeleteThumbnail = useCallback(() =>
	{
		// If thumbnail has a S3 source and key, show confirmation dialog
		if (thumbnail?.source === 'system' && thumbnail?.key)
		{
			updateState({ deleteDialogOpen: true });
		}
		else
		{
			// Otherwise just clear the URL
			updateState({
				thumbnail : {
					...DEFAULT_THUMBNAIL,
					url : ''
				}
			});
		}
	}, [ thumbnail, updateState ]);

	// Handle thumbnail update after selection
	const handleThumbnailUpdate = useCallback((newUrl: string, source?: string, key?: string) =>
	{
		updateState({
			thumbnail : {
				url       : newUrl,
				source    : source || 'system',
				key,
				canEdit   : true,
				canDelete : true
			}
		});
	}, [ updateState ]);

	// Delete file from S3
	const deleteFileFromS3 = useCallback(async (key?: string) =>
	{
		if (!key)
		{
			console.warn('No key provided for S3 deletion');

			return;
		}

		try
		{
			await s3DeleteNew(key, '');
			toast.success('Thumbnail deleted successfully');
		}
		catch (error: any)
		{
			console.error('Error deleting file from S3:', error);
			toast.error(`Failed to delete thumbnail: ${error.message || 'Unknown error'}`);
		}
	}, []);

	// Check if thumbnail URL exists to conditionally show delete button
	const hasThumbnail = Boolean(thumbnail?.url);

	return (
		<>
			<Typography variant='body2' color={'#353535'} fontWeight={'var(--font500)'}>{language?.createNewPlaylist}</Typography>
			<TextField
				fullWidth
				placeholder='Playlist Name'
				margin='dense'
				size='small'
				value={createdPlaylist || inputValue}
				onChange={handleInputChange}
				disabled={Boolean(createdPlaylist) || isCreating}
				InputProps={{
					style        : createdPlaylist ? { color: '#24a4c4', fontWeight: 500 } : undefined,
					endAdornment : isCreating
						? (
							<InputAdornment position='end'>
								<CircularProgress size={20} />
							</InputAdornment>
						)
						: undefined
				}}
			/>
			<Typography variant='body2' pt={2} fontWeight={'var(--font500)'}>
				{language?.description}
			</Typography>
			<TextField
				fullWidth
				multiline
				rows={3}
				placeholder={language?.enterDescript}
				margin='dense'
				value={description}
				onChange={handleDescriptionChange}
				inputProps={{ maxLength: descriptionMaxLength }}
				disabled={Boolean(createdPlaylist)}
			/>
			<Typography textAlign={'end'} color={'#707070'} width={'100%'} fontSize={'var(--pixel12)'}>
				{description.length}/{descriptionMaxLength}
			</Typography>

			{/* Playlist Thumbnail Section */}
			<Typography variant='body2' pt={1} fontWeight={'var(--font500)'}>
				{language?.playlistThumbnail || 'Playlist Thumbnail'}
			</Typography>

			{hasThumbnail
				? <Stack flexDirection={'column'}>
					<Box my={1}
						sx={{ width: '250px', height: '150px', position: 'relative', borderRadius: '13px', overflow: 'hidden' }}
						onMouseEnter={() => toggleHover(true)}
						onMouseLeave={() => toggleHover(false)}
					>
						<img
							src={thumbnail.url}
							style={{ width: '100%', height: '100%', objectFit: 'cover', overflow: 'hidden', borderRadius: '13px' }}
							alt=''
						/>
						{hover && (
							<Box
								width='100%'
								height='100%'
								sx={{
									position       : 'absolute',
									top            : 0,
									left           : 0,
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center',
									background     : 'rgba(0, 0, 0, 0.6)',
									borderRadius   : '13px'
								}}
							>
								<Stack direction={'column'} alignItems={'center'}>
									<Box
										width={'36px'}
										borderRadius={'50%'}
										bgcolor={'#D9D9D9'}
										height={'36px'}
										display={'flex'}
										alignItems={'center'}
										justifyContent={'center'}
										sx={{ cursor: 'pointer' }}
										onClick={handleReplaceThumbnail}
									>
										<TbReplace fontSize='var(--pixel20)' />
									</Box>
									<Typography fontSize={'var(--pixel10)'} color={'var(--whiteColor)'}>{language?.replace}</Typography>
								</Stack>
								{hasThumbnail && (
									<Stack direction={'column'} px={2.5} alignItems={'center'}>
										<Box
											width={'36px'}
											borderRadius={'50%'}
											bgcolor={'#D9D9D9'}
											height={'36px'}
											display={'flex'}
											alignItems={'center'}
											justifyContent={'center'}
											sx={{ cursor: 'pointer' }}
											onClick={handleDeleteThumbnail}
										>
											<RiDeleteBinLine fontSize='var(--pixel20)' />
										</Box>
										<Typography fontSize={'var(--pixel10)'} color={'var(--whiteColor)'}>{language?.delete}</Typography>
									</Stack>
								)}
								<Stack direction={'column'} alignItems={'center'}>
									<Box width={'36px'} borderRadius={'50%'} bgcolor={'#D9D9D9'} height={'36px'} display={'flex'} alignItems={'center'} justifyContent={'center'} sx={{ cursor: 'pointer' }}>
										<MdCrop fontSize='var(--pixel20)' />
									</Box>
									<Typography fontSize={'var(--pixel10)'} color={'var(--whiteColor)'}>{language?.crop}</Typography>
								</Stack>
							</Box>
						)}
					</Box>
				</Stack>
				: 	(
					<Box
						width={'100%'}
						sx={{
							border          : '1px dashed #24a4c4',
							backgroundColor : '#F7FCFD',
							padding         : 2,
							textAlign       : 'center',
							borderRadius    : 2,
							cursor          : 'pointer',
							display         : 'flex',
							justifyContent  : 'center',
							my              : 3
						}}
						onClick={handleOpenThumbnail}
					>
						<Typography fontSize={16} display={'flex'} color='#737373' alignItems={'center'}>
							<AddIcon fontSize='large' sx={{ color: '#737373' }}/>&nbsp;{language?.setthumbail}
						</Typography>
					</Box>
				)}
			<Button
				variant='contained'
				size='small'
				sx={{
					color           : 'var(--whiteColor)',
					marginTop       : 1,
					backgroundColor : '#24A4C4',
					'&:hover'       : {
						backgroundColor : '#1D8BA3'
					},
					'&.Mui-disabled' : {
						backgroundColor : '#E0E0E0',
						color           : '#9E9E9E'
					}
				}}
				fullWidth
				disabled={!inputValue.trim() || isCreating || Boolean(createdPlaylist)}
				onClick={handleCreatePlaylist}
			>
				{isCreating ? 'Creating...' : language?.createPlaylist}
			</Button>
			<UploadDialog
				open={uploadDialogOpen}
				onClose={() => updateState({ uploadDialogOpen: false })}
				fileType='Image'
				note={language?.supportformate}
				onFileSelect={(url: string, source?: string, key?: string) =>
				{
					// If we're replacing an existing thumbnail from S3, delete the old one
					if (thumbnail?.source === 'system' && thumbnail?.key)
					{
						deleteFileFromS3(thumbnail.key);
					}
					handleThumbnailUpdate(url, source, key);
					updateState({ uploadDialogOpen: false });
				}}
				Embednote='Only Upload JPEG, JPG and PNG formats.'
			/>

			{/* Delete confirmation dialog */}
			<NewDialogPopup
				open={deleteDialogOpen}
				handleConfirm={() => updateState({ deleteDialogOpen: false })}
				type={language?.no || 'No'}
				id={undefined}
				label={`${language?.LikeToDeleteThe || 'Would you like to delete the'} thumbnail?`}
				from='feedCreate'
				formatType={language?.yes || 'Yes'}
				closeCallBack={async () =>
				{
					// Check if the thumbnail is from S3 and has a key
					if (thumbnail?.source === 'system' && thumbnail?.key)
					{
						await deleteFileFromS3(thumbnail.key);
					}

					// Reset thumbnail state
					handleThumbnailUpdate('');
					updateState({ deleteDialogOpen: false });
				}}
			/>
		</>
	);
};

export default CreatePlaylist;